/**
 * Employee Time Portal — which experience a signed-in user sees (handoff D3).
 *
 *   employee-only user       → always 'employee' (no pill, no Admin anything)
 *   admin + employee roles   → their last choice, persisted per user in localStorage
 *                              (default 'admin', so nothing changes for admins until they switch)
 *   admin only, or the tenant has no enabled portal → always 'admin'
 *
 * Pure apart from the injected Storage.
 */

export type Experience = 'admin' | 'employee';

export interface AccessFlags {
  adminAccess: boolean;
  employeeAccess: boolean;
  /** config.employeePortal?.enabled */
  portalEnabled: boolean;
}

export const EXPERIENCE_KEY_PREFIX = 'etp.experience.';

/** Per-user key: the AdminEmployeeID (every portal login has one). */
export function experienceKey(userID: number): string {
  return `${EXPERIENCE_KEY_PREFIX}${userID}`;
}

/** Shows the Employee | Admin pill and the user menu's Switch experience. */
export function canSwitchExperience(flags: AccessFlags): boolean {
  return flags.adminAccess && flags.employeeAccess && flags.portalEnabled;
}

export function resolveExperience(flags: AccessFlags, stored: string | null | undefined): Experience {
  if (!flags.portalEnabled || !flags.employeeAccess) return 'admin';
  if (!flags.adminAccess) return 'employee';
  return stored === 'employee' ? 'employee' : 'admin';
}

interface MinimalStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readStoredExperience(storage: MinimalStorage | null, userID: number): Experience | null {
  if (!storage || !userID) return null;
  try {
    const v = storage.getItem(experienceKey(userID));
    return v === 'employee' || v === 'admin' ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredExperience(storage: MinimalStorage | null, userID: number, experience: Experience): void {
  if (!storage || !userID) return;
  try {
    storage.setItem(experienceKey(userID), experience);
  } catch {
    /* private mode / blocked storage: the choice just isn't remembered */
  }
}

/** window.localStorage / sessionStorage, or null where they throw (private mode, tests). */
export function safeStorage(kind: 'local' | 'session'): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}
